import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import { awardXp } from "../../lib/gamification";
import { subscribeLessonContentForModule } from "../../lib/firebase";
import Markdown from "../../components/Markdown/Markdown";
import LessonVideoPlayer from "../lessonvideo/LessonVideoPlayer";

const Section = styled.section`
  padding: 48px 0;
  background: var(--nd-black);
`;

const Kicker = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin: 0 0 12px;
`;

const Heading = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin: 0 0 24px;
`;

const List = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
  border-top: 1px solid var(--nd-border);
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid var(--nd-border);
  flex-wrap: wrap;
`;

const RowIndex = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  color: var(--nd-text-disabled);
  min-width: 28px;
`;

const RowTitle = styled.span`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  color: var(--nd-text-primary);
  flex: 1;
  line-height: 1.4;
`;

const RowDuration = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  color: var(--nd-text-secondary);
  min-width: 56px;
  text-align: right;
`;

const FreeBadge = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-success);
  border: 1px solid var(--nd-success);
  padding: 2px 6px;
  border-radius: 4px;
`;

const MarkBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  background: transparent;
  border: 1px solid var(--nd-border-visible);
  color: ${({ done }) => done ? "var(--nd-text-display)" : "var(--nd-text-disabled)"};
  width: 24px;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  &:hover { border-color: var(--nd-text-display); }
`;

const Toggle = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-primary);
  background: transparent;
  border: 1px solid var(--nd-border-visible);
  border-radius: 999px;
  padding: 10px 18px;
  margin-top: 16px;
  min-height: 44px;
  cursor: pointer;
  transition: border-color 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  &:hover { border-color: var(--nd-text-primary); }
`;

const ReadBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-accent, #7c6fff);
  background: transparent;
  border: 1px solid var(--nd-accent, #7c6fff);
  border-radius: 999px;
  padding: 4px 12px;
  cursor: pointer;
  transition: opacity 150ms ease;
  &:hover { opacity: 0.75; }
`;

const WatchBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-warning, #f0a500);
  background: transparent;
  border: 1px solid var(--nd-warning, #f0a500);
  border-radius: 999px;
  padding: 4px 12px;
  cursor: pointer;
  transition: opacity 150ms ease;
  &:hover { opacity: 0.75; }
`;

const MarkReadBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-success);
  background: transparent;
  border: 1px solid var(--nd-success);
  border-radius: 999px;
  padding: 4px 12px;
  cursor: pointer;
  transition: opacity 150ms ease;
  &:hover { opacity: 0.75; }
`;

const BodyWrap = styled.div`
  width: 100%;
  padding: 16px 0 8px;
  border-top: 1px solid var(--nd-border-visible);
`;

const BodyActions = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 12px;
  align-items: center;
`;

const formatDuration = (min) => {
  const m = Math.floor(min);
  const s = Math.round((min - m) * 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const COLLAPSED_COUNT = 4;

const LessonList = ({ lessons, moduleId }) => {
  const [expanded, setExpanded] = useState(false);
  const [doneIds, setDoneIds] = useState(new Set());
  const [openIds, setOpenIds] = useState(new Set());
  const [watchIds, setWatchIds] = useState(new Set());
  const [contentByLesson, setContentByLesson] = useState({});
  const { user } = useAuth();

  // Stable script objects per lesson — rebuilt only when contentByLesson changes.
  // Keying the player by lessonId (see render) ensures remount on lesson switch;
  // this memo prevents churn from unrelated re-renders within the same lesson.
  const scriptByLesson = useMemo(() => {
    const map = {};
    Object.entries(contentByLesson).forEach(([lessonId, c]) => {
      if (c && c.scriptApproved) {
        map[lessonId] = { voice: c.voice, beats: c.beats };
      }
    });
    return map;
  }, [contentByLesson]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const unsub = subscribeLessonContentForModule(moduleId, setContentByLesson);
    return unsub;
  }, [moduleId]);

  const toggleDone = (lessonId) => {
    setDoneIds((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) {
        next.delete(lessonId);
        return next;
      }
      next.add(lessonId);
      if (user) {
        awardXp({ event: "lesson.completed", payload: { lessonId } });
        // Fire module-completed when the last lesson flips to done.
        // Server dedup (idempotencyKey=moduleId) ensures repeat fires
        // from refreshing the page or re-checking are no-ops.
        if (moduleId && next.size === lessons.length) {
          awardXp({
            event: "course.module_completed",
            payload: { moduleId },
          });
        }
      }
      return next;
    });
  };

  const toggleOpen = (lessonId) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) { next.delete(lessonId); } else { next.add(lessonId); }
      return next;
    });
  };

  const handleMarkRead = (lessonId) => {
    if (!doneIds.has(lessonId)) {
      toggleDone(lessonId);
    }
  };

  const toggleWatch = (lessonId) => {
    setWatchIds((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) { next.delete(lessonId); } else { next.add(lessonId); }
      return next;
    });
  };

  // Called when the video player fires onComplete. Marks the lesson done and
  // awards XP exactly like the "Mark as read" path; server deduplicates both.
  const handleComplete = (lessonId) => {
    if (!doneIds.has(lessonId)) {
      toggleDone(lessonId);
    }
  };

  const visible = expanded ? lessons : lessons.slice(0, COLLAPSED_COUNT);
  const hidden = lessons.length - COLLAPSED_COUNT;
  return (
    <Section>
      <Container>
        <Kicker>CURRICULUM</Kicker>
        <Heading>{lessons.length} lessons</Heading>
        <List>
          {visible.map((l, i) => {
            const content = contentByLesson[l.id];
            const hasContent = content && content.published;
            const hasVideo = Boolean(scriptByLesson[l.id]);
            const isOpen = openIds.has(l.id);
            const isWatching = watchIds.has(l.id);
            return (
              <Row key={l.id}>
                <MarkBtn
                  done={doneIds.has(l.id) ? 1 : 0}
                  onClick={() => toggleDone(l.id)}
                  aria-label={doneIds.has(l.id) ? "Mark incomplete" : "Mark complete"}
                >
                  {doneIds.has(l.id) ? "✓" : ""}
                </MarkBtn>
                <RowIndex>{String(i + 1).padStart(2, "0")}</RowIndex>
                <RowTitle>{l.title}</RowTitle>
                {l.free && <FreeBadge>Free</FreeBadge>}
                {hasContent && (
                  <ReadBtn
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => toggleOpen(l.id)}
                  >
                    {isOpen ? "Close" : "Read"}
                  </ReadBtn>
                )}
                {hasVideo && (
                  <WatchBtn
                    type="button"
                    aria-expanded={isWatching}
                    onClick={() => toggleWatch(l.id)}
                  >
                    {isWatching ? "Close" : "▶ Watch"}
                  </WatchBtn>
                )}
                <RowDuration>{formatDuration(l.durationMin)}</RowDuration>
                {hasContent && isOpen && (
                  <BodyWrap>
                    <Markdown source={content.body} />
                    <BodyActions>
                      <MarkReadBtn
                        type="button"
                        onClick={() => handleMarkRead(l.id)}
                        disabled={doneIds.has(l.id)}
                      >
                        {doneIds.has(l.id) ? "Completed" : "Mark as read"}
                      </MarkReadBtn>
                    </BodyActions>
                  </BodyWrap>
                )}
                {hasVideo && isWatching && (
                  <BodyWrap>
                    <LessonVideoPlayer
                      key={l.id}
                      script={scriptByLesson[l.id]}
                      onComplete={() => handleComplete(l.id)}
                    />
                  </BodyWrap>
                )}
              </Row>
            );
          })}
        </List>
        {hidden > 0 && (
          <Toggle
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((x) => !x)}
          >
            {expanded ? "Collapse" : `Show all ${lessons.length} lessons`}
          </Toggle>
        )}
      </Container>
    </Section>
  );
};

export default LessonList;
